import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AnimateHeight from "react-animate-height";
import { useTranslation } from 'react-i18next';
import { useMinWidth } from "../../hooks/media-query";
import AppIcon from "../AppIcon";
import "../../assets/styles/components/profileNavbar.css"; // Component-specific (allowed by guidelines for exclusive styles)

export type NavbarProps = {
  title?: string;
  logo?: string;
  items?: NavbarItemProps[];
  sideItems?: NavbarItemProps[];
  primaryColor?: string; // prefer CSS var overrides externally
  fullName?: string;
  imageUrl?: string;
  jobTitle?: string;
  department?: string;
  onImageChange?: () => void; // Callback to trigger image change
  canEditImage?: boolean; // Whether user can edit their image
};

export type NavbarItemProps = {
  id: string; // used for selection / route matching
  to?: string;
  content: React.JSX.Element | string;
  menu?: Array<any>; // legacy support; consider migrating to centralized menu pattern
  onClick?: () => void;
  primaryColor?: string;
};

function ProfileNavbar({ items, sideItems, fullName, imageUrl, jobTitle, department, primaryColor, onImageChange, canEditImage }: NavbarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const lg = useMinWidth(920);
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | undefined>(undefined);
  const [imgError, setImgError] = useState(false);
  const navigate = useNavigate();

  // Generate initials from full name for fallback avatar
  const initials = fullName
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  // Reset image error when URL changes (e.g. after fresh API data arrives)
  useEffect(() => { setImgError(false); }, [imageUrl]);

  // Check if image URL is valid (not empty, not just server URL)
  const hasValidImage = !imgError && imageUrl && !imageUrl.endsWith('/') && !imageUrl.endsWith('null') && !imageUrl.endsWith('undefined');

  // Derive selected index from route changes (no state update during render)
  useEffect(() => {
    if (!items) return;
    const idx = items.findIndex(i => i.to && currentPath.includes(i.id));
    if (idx >= 0 && idx !== selectedIndex) setSelectedIndex(idx);
  }, [currentPath, items, selectedIndex]);

  const navbarItemCallback = useCallback((item: NavbarItemProps, i?: number) => () => {
    item.onClick?.();
    if (item.to) {
      // Server-side routes (e.g., Identity pages) need full page navigation, not React Router
      if (item.to.startsWith('/Account/')) {
        window.location.href = item.to;
      } else {
        navigate(item.to);
      }
    }
    setExpanded(false);
    setSelectedIndex(i ?? 0);
  }, [navigate]);

  if (lg) {
    return (
      <header className="bg-gradient-to-r from-primary to-primary-dark text-white relative" role="banner">
        {/* Side items pinned to top-end corner */}
        {sideItems && sideItems.length > 0 && (
          <div className="absolute top-4 end-4 flex gap-2">
            {sideItems.map((item) => (
              <button
                key={item.id}
                onClick={navbarItemCallback(item)}
                className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium whitespace-nowrap"
              >
                {item.content}
              </button>
            ))}
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {/* Top section: Avatar + Info */}
          <div className="flex items-center gap-6 mb-6">
            <div className="relative group">
              {hasValidImage ? (
                <img
                  src={imageUrl}
                  alt={fullName || t('Profile')}
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div
                  className="w-24 h-24 rounded-full border-4 border-white shadow-lg flex items-center justify-center"
                  style={{
                    background: `linear-gradient(to bottom right, ${primaryColor || '#304EA1'}, #6366f1)`
                  }}
                >
                  <span className="text-white text-2xl font-semibold">{initials}</span>
                </div>
              )}
              {canEditImage && onImageChange && (
                <button
                  onClick={onImageChange}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors border-2 border-white cursor-pointer"
                  title={t('Change profile picture')}
                >
                  <AppIcon name="Pencil" size={16} style={{ color: primaryColor || '#304EA1' }} />
                </button>
              )}
              {!canEditImage && (
                <span className="absolute bottom-0 right-0 w-4 h-4 bg-green-500 border-2 border-white rounded-full" />
              )}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{fullName || ''}</h1>
              {(jobTitle || department) && (
                <p className="text-white/80">
                  {jobTitle}{jobTitle && department && ' | '}{department}
                </p>
              )}
            </div>
          </div>

          {/* Tab navigation */}
          <nav className="flex gap-1 overflow-x-auto" role="tablist">
            {items?.map((item, i) => (
              <button
                key={item.id}
                onClick={navbarItemCallback(item, i)}
                role="tab"
                aria-selected={selectedIndex === i}
                className={`px-4 py-2 rounded-t-lg font-medium transition-colors whitespace-nowrap ${
                  selectedIndex === i
                    ? 'bg-white text-primary'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                {item.content}
              </button>
            ))}
          </nav>
        </div>

      </header>
    );
  }

  // Mobile
  return (
    <header className="bg-gradient-to-r from-primary to-primary-dark text-white" role="banner">
      <div className="px-4 py-4">
        {/* Mobile top bar */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              {hasValidImage ? (
                <img
                  src={imageUrl}
                  alt={fullName || t('Profile')}
                  className="w-12 h-12 rounded-full border-2 border-white object-cover"
                  onError={() => setImgError(true)}
                />
              ) : (
                <div
                  className="w-12 h-12 rounded-full border-2 border-white flex items-center justify-center"
                  style={{
                    background: `linear-gradient(to bottom right, ${primaryColor || '#304EA1'}, #6366f1)`
                  }}
                >
                  <span className="text-white text-sm font-semibold">{initials}</span>
                </div>
              )}
              {canEditImage && onImageChange && (
                <button
                  onClick={onImageChange}
                  className="absolute -bottom-1 -right-1 w-6 h-6 bg-white rounded-full shadow-md flex items-center justify-center hover:bg-gray-100 transition-colors border border-white cursor-pointer"
                  title={t('Change profile picture')}
                >
                  <AppIcon name="Pencil" size={12} style={{ color: primaryColor || '#304EA1' }} />
                </button>
              )}
            </div>
            <div>
              <h1 className="text-lg font-bold">{fullName || ''}</h1>
              {jobTitle && <p className="text-sm text-white/80">{jobTitle}</p>}
            </div>
          </div>
          <button
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            aria-label={expanded ? t('Collapse') : t('Expand')}
            className="p-2 rounded-full hover:bg-white/20 transition-colors text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {expanded ? <AppIcon name="X" size={24} className="text-white" /> : <AppIcon name="Menu" size={24} className="text-white" />}
          </button>
        </div>

        {/* Mobile navigation */}
        <AnimateHeight height={expanded ? "auto" : 0}>
          <nav className="flex flex-col gap-1 pb-4" role="tablist">
            {items?.map((item, i) => (
              <button
                key={item.id}
                onClick={navbarItemCallback(item, i)}
                role="tab"
                aria-selected={selectedIndex === i}
                className={`px-4 py-3 rounded-lg text-left font-medium transition-colors ${
                  selectedIndex === i
                    ? 'bg-white text-primary'
                    : 'text-white/80 hover:bg-white/10'
                }`}
              >
                {item.content}
              </button>
            ))}
          </nav>
        </AnimateHeight>
      </div>
    </header>
  );
}

export default ProfileNavbar as React.FunctionComponent<NavbarProps>;
